/**
 * Workspace-level custom field metadata. The leads table renders one
 * column per entry; cell values are read from each lead's custom_fields
 * dict by `name`. Order is determined server-side (alphabetical by
 * name) so re-renders never reshuffle columns.
 */
export interface WorkspaceCustomField {
  name: string;
  label: string;
}

export interface LeadRow {
  id: number;
  name?: string | null;
  email: string;
  phone?: string | null;
  date: string;
  custom_fields?: Record<string, string | undefined>;
  website?: { id: number; name: string };
  page?: { id: number; title: string } | null;
}

interface LeadsTableProps {
  leads: LeadRow[];
  workspaceCustomFields?: WorkspaceCustomField[];
  showWebsite?: boolean;
  showPage?: boolean;
}

const EmDash = () => <span className="text-base-400">&mdash;</span>;

export function LeadsTable({
  leads,
  workspaceCustomFields = [],
  showWebsite = false,
  showPage = false,
}: LeadsTableProps) {
  return (
    <table className="w-full" data-testid="leads-table">
      <thead className="bg-neutral-50 border-b border-neutral-200">
        <tr>
          <th className="text-left px-6 py-3 text-sm font-medium text-base-600">Name</th>
          <th className="text-left px-6 py-3 text-sm font-medium text-base-600">Phone</th>
          <th className="text-left px-6 py-3 text-sm font-medium text-base-600">Email</th>
          <th className="text-left px-6 py-3 text-sm font-medium text-base-600">Date</th>
          {showWebsite && (
            <th className="text-left px-6 py-3 text-sm font-medium text-base-600">Website</th>
          )}
          {showPage && (
            <th className="text-left px-6 py-3 text-sm font-medium text-base-600">Page</th>
          )}
          {workspaceCustomFields.map((cf) => (
            <th
              key={cf.name}
              className="text-left px-6 py-3 text-sm font-medium text-base-600"
              data-testid={`custom-field-header-${cf.name}`}
            >
              {cf.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {leads.map((lead) => (
          <tr
            key={lead.id}
            className="border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50"
            data-testid="lead-row"
          >
            <td className="px-6 py-4 text-sm text-base-600">
              {lead.name || <EmDash />}
            </td>
            <td className="px-6 py-4 text-sm text-base-600">
              {lead.phone || <EmDash />}
            </td>
            <td className="px-6 py-4 text-sm text-base-600">{lead.email}</td>
            <td className="px-6 py-4 text-sm text-base-500">{lead.date}</td>
            {showWebsite && (
              <td className="px-6 py-4 text-sm text-base-600" data-testid="lead-website">
                {lead.website?.name || <EmDash />}
              </td>
            )}
            {showPage && (
              <td className="px-6 py-4 text-sm text-base-600" data-testid="lead-page">
                {lead.page?.title || <EmDash />}
              </td>
            )}
            {workspaceCustomFields.map((cf) => {
              const value = lead.custom_fields?.[cf.name];
              return (
                <td
                  key={cf.name}
                  className="px-6 py-4 text-sm text-base-600"
                  data-testid={`custom-field-cell-${cf.name}`}
                >
                  {value || <EmDash />}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}
